import styled, { css } from 'styled-components';
import { withTheme } from '../../core/ThemeProvider';
import { responsiveStyles } from '../../core/utils/responsiveStyles';
import getFontFamily from '../../helpers/getFontFamily';

const bodyLineHeights = { default: 1.75, compact: 1.5, medium: 1.3 };
const fontWeights = { thin: 'thin', regular: 'regular', bold: 'bold' };
const fontFamilyType = { primary: 'primary', secondary: 'secondary' };

/**
 * CSS styles for the type system
 */
export const typePreset = {
  bannerTitle: {
    tag: 'h1',
    style: ({ coreTheme, color, fontWeight, fontSize }) => ({
      ...typeReset,
      ...responsiveStyles(
        {
          fontSize: fontSize ||
            coreTheme.text?.bannerTitle?.fontSize || [32, 32, 36, 48, 48]
        },
        coreTheme
      ),
      fontFamily: getFontFamily({
        fontFamilyType:
          coreTheme.text?.fontFamilyType || fontFamilyType.primary,
        fontWeight:
          fontWeight ||
          coreTheme.text?.bannerTitle?.fontWeight ||
          fontWeights.thin,
        theme: coreTheme,
        typePreset: 'bannerTitle'
      }),
      fontWeight: coreTheme.text?.bannerTitle?.fontWeight || 'unset',
      color:
        color || coreTheme.text?.bannerTitle?.color || coreTheme.cl_ter_base,
      lineHeight: coreTheme.text?.bannerTitle?.lineHeight || 1.3,
      ...(coreTheme.text?.bannerTitle?.letterSpacing
        ? {
            letterSpacing: coreTheme.text?.bannerTitle?.letterSpacing
          }
        : { letterSpacing: 0.1 })
    })
  },
  pageTitle: {
    tag: 'h2',
    style: ({ coreTheme, color, fontWeight, fontSize }) => ({
      ...typeReset,
      ...responsiveStyles(
        {
          fontSize: fontSize ||
            coreTheme.text?.pageTitle?.fontSize || [32, 32, 36, 40, 40]
        },
        coreTheme
      ),
      fontFamily: getFontFamily({
        fontFamilyType:
          coreTheme.text?.fontFamilyType || fontFamilyType.primary,
        fontWeight:
          fontWeight ||
          coreTheme.text?.pageTitle?.fontWeight ||
          fontWeights.regular,
        theme: coreTheme,
        typePreset: 'pageTitle'
      }),
      fontWeight: coreTheme.text?.pageTitle?.fontWeight || 'unset',
      color: color || coreTheme.text?.pageTitle?.color || coreTheme.cl_ter_base,
      lineHeight: coreTheme.text?.pageTitle?.lineHeight || 1.35,
      ...(coreTheme.text?.pageTitle?.letterSpacing
        ? {
            letterSpacing: coreTheme.text?.pageTitle?.letterSpacing
          }
        : { letterSpacing: 0.1 })
    })
  },
  sectionTitle: {
    tag: 'h3',
    style: ({ coreTheme, color, fontWeight, fontSize }) => ({
      ...typeReset,
      ...responsiveStyles(
        {
          fontSize: fontSize ||
            coreTheme.text?.sectionTitle?.fontSize || [24, 24, 28, 32, 32]
        },
        coreTheme
      ),
      fontFamily: getFontFamily({
        fontFamilyType:
          coreTheme.text?.fontFamilyType || fontFamilyType.primary,
        fontWeight:
          fontWeight ||
          coreTheme.text?.sectionTitle?.fontWeight ||
          fontWeights.regular,
        theme: coreTheme,
        typePreset: 'sectionTitle'
      }),
      fontWeight: coreTheme.text?.sectionTitle?.fontWeight || 'unset',
      color:
        color || coreTheme.text?.sectionTitle?.color || coreTheme.cl_ter_base,
      lineHeight: coreTheme.text?.sectionTitle?.lineHeight || 1.35,
      ...(coreTheme.text?.sectionTitle?.letterSpacing
        ? {
            letterSpacing: coreTheme.text?.sectionTitle?.letterSpacing
          }
        : { letterSpacing: 0.1 })
    })
  },
  sectionTitle02: {
    tag: 'h3',
    style: ({ coreTheme, color, fontWeight, fontSize }) => ({
      ...typeReset,
      ...responsiveStyles(
        {
          fontSize: fontSize ||
            coreTheme.text?.sectionTitle02?.fontSize || [24, 24, 28, 32, 32]
        },
        coreTheme
      ),
      fontFamily: getFontFamily({
        fontFamilyType:
          coreTheme.text?.fontFamilyType || fontFamilyType.secondary,
        fontWeight:
          fontWeight ||
          coreTheme.text?.sectionTitle02?.fontWeight ||
          fontWeights.bold,
        theme: coreTheme,
        typePreset: 'sectionTitle02'
      }),
      fontWeight: coreTheme.text?.sectionTitle02?.fontWeight || 'unset',
      color:
        color || coreTheme.text?.sectionTitle02?.color || coreTheme.cl_ter_base,
      lineHeight: coreTheme.text?.sectionTitle02?.lineHeight || 1.35,
      ...(coreTheme.text?.sectionTitle02?.letterSpacing
        ? {
            letterSpacing: coreTheme.text?.sectionTitle02?.letterSpacing
          }
        : { letterSpacing: 0.1 })
    })
  },
  header: {
    tag: 'h4',
    style: ({ coreTheme, color, fontWeight, fontSize }) => ({
      ...typeReset,
      ...responsiveStyles(
        {
          fontSize: fontSize ||
            coreTheme.text?.header?.fontSize || [20, 20, 22, 24, 24]
        },
        coreTheme
      ),
      fontFamily: getFontFamily({
        fontFamilyType:
          coreTheme.text?.fontFamilyType || fontFamilyType.secondary,
        fontWeight:
          fontWeight || coreTheme.text?.header?.fontWeight || fontWeights.bold,
        theme: coreTheme,
        typePreset: 'header'
      }),
      fontWeight: coreTheme.text?.header?.fontWeight || 'unset',
      color: color || coreTheme.text?.header?.color || coreTheme.cl_ter_base,
      lineHeight: coreTheme.text?.header?.lineHeight || 1.35,
      ...responsiveStyles(
        {
          ...(coreTheme.text?.header?.letterSpacing
            ? {
                letterSpacing: coreTheme.text?.header?.letterSpacing
              }
            : { letterSpacing: 0.1 })
        },
        coreTheme
      )
    })
  },
  boldBody: {
    tag: 'p',
    style: ({ coreTheme, lineHeight, color, fontWeight, fontSize }) => ({
      ...typeReset,
      ...responsiveStyles(
        {
          fontSize: fontSize ||
            coreTheme.text?.boldBody?.fontSize || [16, 16, 18, 20, 20]
        },
        coreTheme
      ),
      fontFamily: getFontFamily({
        fontFamilyType:
          coreTheme.text?.fontFamilyType || fontFamilyType.secondary,
        fontWeight:
          fontWeight ||
          coreTheme.text?.boldBody?.fontWeight ||
          fontWeights.bold,
        theme: coreTheme,
        typePreset: 'boldBody'
      }),
      fontWeight: coreTheme.text?.boldBody?.fontWeight || 'unset',
      color: color || coreTheme.text?.boldBody?.color || coreTheme.cl_ter_base,
      lineHeight:
        coreTheme.text?.boldBody?.lineHeight || bodyLineHeights[lineHeight],
      ...(coreTheme.text?.boldBody?.letterSpacing
        ? {
            letterSpacing: coreTheme.text?.boldBody?.letterSpacing
          }
        : 0)
    })
  },
  body: {
    tag: 'p',
    style: ({
      coreTheme,
      strikeThrough,
      lineHeight,
      color,
      fontWeight,
      fontSize
    }) => ({
      ...typeReset,
      ...responsiveStyles(
        {
          fontSize: fontSize ||
            coreTheme.text?.body?.fontSize || [16, 16, 18, 20, 20]
        },
        coreTheme
      ),
      fontFamily: getFontFamily({
        fontFamilyType:
          coreTheme.text?.fontFamilyType || fontFamilyType.secondary,
        fontWeight:
          fontWeight || coreTheme.text?.body?.fontWeight || fontWeights.thin,
        theme: coreTheme,
        typePreset: 'body'
      }),
      fontWeight: coreTheme.text?.body?.fontWeight || 'unset',
      ...(strikeThrough && { textDecoration: 'line-through' }),
      color: color || coreTheme.text?.body?.color || coreTheme.cl_ter_base,
      lineHeight:
        coreTheme.text?.body?.lineHeight || bodyLineHeights[lineHeight],
      ...(coreTheme.text?.body?.letterSpacing
        ? {
            letterSpacing: coreTheme.text?.body?.letterSpacing
          }
        : { letterSpacing: 0.1 })
    })
  },
  smallBody: {
    tag: 'p',
    style: ({
      coreTheme,
      strikeThrough,
      lineHeight,
      color,
      fontWeight,
      fontSize
    }) => ({
      ...typeReset,
      ...responsiveStyles(
        {
          fontSize: fontSize ||
            coreTheme.text?.smallBody?.fontSize || [14, 14, 15, 16, 16]
        },
        coreTheme
      ),
      fontFamily: getFontFamily({
        fontFamilyType:
          coreTheme.text?.fontFamilyType || fontFamilyType.secondary,
        fontWeight:
          fontWeight ||
          coreTheme.text?.smallBody?.fontWeight ||
          fontWeights.thin,
        theme: coreTheme,
        typePreset: 'smallBody'
      }),
      fontWeight:
        fontWeight === 'semibold'
          ? coreTheme?.text?.smallBody?.fontWeightSemiBold
          : coreTheme?.text?.smallBody?.fontWeight || 'unset',
      ...(strikeThrough && { textDecoration: 'line-through' }),
      color: color || coreTheme.text?.smallBody?.color || coreTheme.cl_ter_base,
      lineHeight:
        coreTheme.text?.smallBody?.lineHeight || bodyLineHeights[lineHeight],
      ...responsiveStyles(
        {
          ...(coreTheme.text?.smallBody?.letterSpacing
            ? {
                letterSpacing:
                  fontWeight === 'semibold'
                    ? coreTheme.text?.smallBody?.semiBoldLetterSpacing
                    : coreTheme.text?.smallBody?.letterSpacing
              }
            : { letterSpacing: 0.2 })
        },
        coreTheme
      )
    })
  },
  boldSmallBody: {
    tag: 'p',
    style: ({ coreTheme, lineHeight, color, fontWeight, fontSize }) => ({
      ...typeReset,
      ...responsiveStyles(
        {
          fontSize: fontSize ||
            coreTheme.text?.boldSmallBody?.fontSize || [14, 14, 15, 16, 16]
        },
        coreTheme
      ),
      fontFamily: getFontFamily({
        fontFamilyType:
          coreTheme.text?.fontFamilyType || fontFamilyType.secondary,
        fontWeight:
          fontWeight ||
          coreTheme.text?.boldSmallBody?.fontWeight ||
          fontWeights.thin,
        theme: coreTheme,
        typePreset: 'boldSmallBody'
      }),
      fontWeight: coreTheme?.text?.boldSmallBody?.fontWeight || 'unset',
      color:
        color || coreTheme.text?.boldSmallBody?.color || coreTheme.cl_ter_base,
      lineHeight:
        coreTheme.text?.bannerTitle?.lineHeight || bodyLineHeights[lineHeight],
      ...(coreTheme.text?.bannerTitle?.letterSpacing
        ? {
            letterSpacing: coreTheme.text?.bannerTitle?.letterSpacing
          }
        : { letterSpacing: 0.2 })
    })
  },
  link: {
    tag: 'a',
    style: ({ coreTheme, lineHeight, color, fontWeight, fontSize }) => ({
      ...typeReset,
      ...responsiveStyles(
        {
          fontSize: fontSize ||
            coreTheme.text?.link?.fontSize || [16, 16, 18, 20, 20]
        },
        coreTheme
      ),
      fontFamily: getFontFamily({
        fontFamilyType:
          coreTheme.text?.fontFamilyType || fontFamilyType.secondary,
        fontWeight:
          fontWeight || coreTheme.text?.link?.fontWeight || fontWeights.thin,
        theme: coreTheme,
        typePreset: 'link'
      }),
      fontWeight: coreTheme?.text?.boldSmallBody?.fontWeight || 'unset',
      color: color || coreTheme.text?.link?.color || coreTheme.cl_ter_base,
      lineHeight:
        coreTheme.text?.boldSmallBody?.lineHeight ||
        bodyLineHeights[lineHeight],
      ...(coreTheme.text?.boldSmallBody?.letterSpacing
        ? {
            letterSpacing: coreTheme.text?.boldSmallBody?.letterSpacing
          }
        : { letterSpacing: 0.1 })
    })
  }
};

const LinkComponent = styled.a`
  margin-top: 0;
  margin-bottom: 0;
  font-weight: unset;
  letter-spacing: 0;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  cursor: pointer;
  text-decoration: none;

  ${p => ({
    ...(p?.coreTheme?.text?.link
      ? typePreset.link.style(p)
      : typePreset.boldBody.style(p)),
    color: p.coreTheme.cl_sec_d1
  })}
`;

/**
 * Create Styled components using the type system css
 * @type {{}}
 */
export const typeComponents = Object.keys(typePreset).reduce(
  (components, typeName) => {
    const { tag, style } = typePreset[typeName];
    const TypeComponent = styled[tag](
      p => css`
        ${style(p)};

        ${/* sc-selector */ LinkComponent} {
          ${({ coreTheme }) => ({
            ...responsiveStyles(
              {
                fontSize: coreTheme?.text?.link?.fontSize || 'inherit'
              },
              coreTheme
            )
          })};
          letter-spacing: inherit;
          line-height: inherit;
        }
      `
    );
    components[typeName] = withTheme(TypeComponent);
    return components;
  },
  {}
);

typeComponents.link = withTheme(LinkComponent);

/**
 * Default styles for typography
 */
const typeReset = {
  marginTop: 0,
  marginBottom: 0,
  fontWeight: 'unset',
  letterSpacing: 0,
  textRendering: 'optimizeLegibility',
  '-webkit-font-smoothing': 'antialiased'
};
